import { SortChangedEvent } from "ag-grid-community";
import { saveAs } from "file-saver";
import { applySnapshot, types } from "mobx-state-tree";
import { DATE_TIME_FORMAT, DEFAULT_SORTING_ASCENDING_VALUE, EMPTY_OBJECT_ID } from "modules/common/constants";
import { isNewlyCreated } from "modules/common/models/entity";
import { flow } from "modules/common/models/flow";
import { Notificator } from "modules/common/models/notificator";
import { Queryable } from "modules/common/models/queryable";
import { TablePager } from "modules/common/models/table-pager";
import { TableSorter } from "modules/common/models/table-sorter";
import { Transport } from "modules/common/models/transport";
import { apiUrls } from "modules/common/services/communication/urls";
import { base64ToBlob, printPdf } from "modules/common/services/files";
import { formatDate } from "modules/common/services/formatting/date";
import { toJsonHard } from "modules/common/services/mobx/serialize";
import { capitalize, trimStart } from "modules/common/services/strings";
import { getSortOption } from "modules/common/services/table/sorting-storage";
import { texts } from "modules/common/texts";
import {
  OrderConfirm,
  OrderConfirmSnapshotType,
  SaveConfirm,
  confirmFields,
  initialState as emptyConfirm,
} from "modules/orders-manage/models/order-confirm";
import { OrderDictionary, initialState as emptyOrders } from "modules/orders-manage/models/order-dictionary";
import { RenderCache } from "modules/orders-manage/models/render-cache";
import { parse } from "query-string";

const DEFAULT_COLUMN = "Date";

export const OrderConfirmStore = types
  .compose(
    Notificator,
    Transport,
    TablePager,
    Queryable,
    types.model({
      confirms: types.array(OrderConfirm),
      orders: OrderDictionary,
      sorter: TableSorter,
      renderer: RenderCache,
      // filters part
      filtersType: types.string,
      filtersStatus: types.string,
      filtersOrderId: types.string,
    })
  )
  .views((self) => ({
    get queryParams() {
      const filters: TStringMap<any> = {
        page: self.page,
        column: capitalize(self.sorter.column),
        asc: self.sorter.asc,
        itemsPerPage: self.pageSize,
      };

      if (self.filtersOrderId) {
        filters.orderId = self.filtersOrderId;
      }

      if (self.filtersType) {
        filters.type = self.filtersType;
      }

      if (self.filtersStatus) {
        filters.status = self.filtersStatus;
      }

      if (self.pureQuery) {
        filters.complexSearch = self.pureQuery;
      }

      return filters;
    },

    get data() {
      return self.confirms.map((m) => {
        return toJsonHard(m);
      });
    },
  }))
  .actions((self) => {
    function loadData() {
      return self.transport.post<PagedList<OrderConfirmSnapshotType>>(
        apiUrls.orders.orderConfirm.list,
        self.queryParams
      );
    }

    return {
      load: flow(function* (sliding = false) {
        try {
          if (!self.filtersOrderId) {
            self.orders.isEmpty && self.orders.load();
          }

          if (self.filtersOrderId === EMPTY_OBJECT_ID) {
            applySnapshot(self.confirms, []);
            self.totalCount = 0;
          } else {
            let confirms: PagedList<OrderConfirmSnapshotType> = yield loadData();

            if (sliding && confirms.items.length === 0 && self.page > 1) {
              self.page = self.page - 1;
              confirms = yield loadData();
            }

            applySnapshot(self.confirms, confirms.items);
            self.totalCount = confirms.totalCount;
          }
          return true;
        } catch (e) {
          self.notify.error(e);
          return false;
        }
      }),

      uploadFile: flow(function* (file: File) {
        try {
          const model = new FormData();

          model.append("file", file);
          model.append("accept", "*");

          const result: UploadFileResult = yield self.transport.post<any>(apiUrls.application.files.upload, model);
          const { id, mimeType, previewMimeType } = result;

          const fileBase: FileBase = { fileId: id, fileName: file.name, previewMimeType, mimeType };
          return fileBase;
        } catch (er) {
          self.notify.error(er);
          return null;
        }
      }),
    };
  })
  .actions((self) => ({
    resorted(e: SortChangedEvent) {
      self.sorter.resorted(e, false);
      self.page = 1;
      self.load();
    },

    setPage(page: number) {
      self.page = page;
      return self.load();
    },

    save: flow(function* (message: SaveConfirm) {
      try {
        const body: TStringMap<any> = { ...message };

        if (body[confirmFields.date] instanceof Date) {
          body[confirmFields.date] = formatDate(body[confirmFields.date], DATE_TIME_FORMAT);
        }

        if (Array.isArray(body[confirmFields.requestFiles])) {
          body[confirmFields.requestFiles] = body[confirmFields.requestFiles].map((doc: FileBase) => doc.fileId);
        }
        if (Array.isArray(body[confirmFields.sourceFiles])) {
          body[confirmFields.sourceFiles] = body[confirmFields.sourceFiles].map((doc: FileBase) => doc.fileId);
        }
        if (Array.isArray(body[confirmFields.resultFiles])) {
          body[confirmFields.resultFiles] = body[confirmFields.resultFiles].map((doc: FileBase) => doc.fileId);
        }

        isNewlyCreated(message.id)
          ? yield self.transport.put<any>(apiUrls.orders.orderConfirm.create, body)
          : yield self.transport.post<any>(apiUrls.orders.orderConfirm.update(message.id), body);

        yield self.load();

        self.notify.success(texts.messages.saved);
        return true;
      } catch (e) {
        self.notify.error(e);
        return false;
      }
    }),

    remove: flow(function* (mails: Identified[]) {
      try {
        const ids = mails.map((m) => m.id);

        yield self.transport.delete<any>(apiUrls.orders.orderConfirm.remove, { data: { ids } });

        self.load(true);

        self.notify.success(texts.messages.removed);
        return true;
      } catch (e) {
        self.notify.error(e);
        return false;
      }
    }),
  }))
  .actions((self) => ({
    setFiltersType(type: string) {
      if (self.filtersType !== type) {
        self.filtersType = type;
        self.setPage(1);
      }
    },

    setOrderId(id?: string): Promise<boolean> {
      const query = ExternalConfirmNumber();
      if (id) {
        self.filtersOrderId = id;
        self.pureQuery = query.toLowerCase();
        self.query = query;
        self.filtersStatus = "";
        self.filtersType = "";
      }
      return self.setPage(1);
    },

    toggleFiltersStatus(status: string) {
      if (!status) {
        self.filtersType = "";
      } else {
        self.filtersType = self.filtersType === status ? "" : status;
      }

      self.setPage(1);
    },

    onQueryChanged() {
      self.setPage(1);
    },
  }))
  .actions((self) => ({
    moveSelection: flow(function* (from: OrderConfirmSnapshotType, offcet: number) {
      const index = self.confirms.findIndex((confirm) => confirm.id === from.id);
      let newIndex = index + offcet;

      // go back
      if (newIndex < 0) {
        if (self.page > 1) {
          self.page = self.page - 1;
          if (yield self.load()) {
            newIndex = self.confirms.length + newIndex;
            return self.confirms[newIndex];
          }
        }
      } else if (newIndex >= self.confirms.length) {
        newIndex = (self.page + 1) * self.pageSize;
        if (newIndex < self.totalCount) {
          self.page = self.page + 1;

          if (yield self.load()) {
            newIndex = 0;
            return self.confirms[newIndex];
          }
        }
      } else {
        return self.confirms[newIndex];
      }
    }),

    printFile: flow(function* (fileId: string) {
      const confirm = self.confirms.find((m) => {
        return !!m.documentsAsMap[fileId];
      });

      if (confirm) {
        const document = confirm.documentsAsMap[fileId];
        yield document.print();
      }
    }),

    printConfirms: flow(function* (confirms: string[]) {
      const source = self.confirms.filter((m) => confirms.includes(m.id));
      if (source.length) {
        const files: string[] = source.reduce((acc, m) => {
          return [...acc, ...m.resultFiles.map((d) => d.id)];
        }, [] as string[]);

        if (files.length) {
          try {
            const content: DownloadFileResult = yield self.transport.post<any>(apiUrls.application.files.print, {
              ids: files,
            });

            if (content) {
              const blob: any = yield base64ToBlob(content.content, content.mimeType);
              const fileURL = URL.createObjectURL(blob);
              const printer = printPdf(fileURL, true);
              if (printer) {
                printer.onclose = () => URL.revokeObjectURL(fileURL);
              }

              return true;
            }
          } catch (er) {
            self.notify.error(er);
            return false;
          }
        }
      }

      return false;
    }),

    downloadFile: flow(function* (fileId: string) {
      const mail = self.confirms.find((m) => {
        return !!m.documentsAsMap[fileId];
      });
      if (mail) {
        const document = mail.documentsAsMap[fileId];
        yield document.download();
        const blob: any = yield base64ToBlob(document.content || "", document.mimeType);
        saveAs(blob, document.name);
      }
    }),

    downloadConfirms: flow(function* (confirms: string[], canClientEdit?: boolean) {
      const source = self.confirms.filter((m) => confirms.includes(m.id));
      if (source.length) {
        let files: string[] = [];
        if (canClientEdit) {
          files = source.reduce((acc, m) => {
            return [...acc, ...m.resultFiles.map((d) => d.id)];
          }, [] as string[]);
        } else {
          files = source.reduce((acc, m) => {
            return [...acc, ...m.sourceFiles.map((d) => d.id).concat(m.resultFiles.map((d) => d.id))];
          }, [] as string[]);
        }

        if (files.length) {
          try {
            const content: DownloadFileResult = yield self.transport.post<any>(apiUrls.application.files.download, {
              ids: files,
            });

            if (content) {
              const blob: any = yield base64ToBlob(content.content, content.mimeType);

              //
              //as pdf const fileURL = URL.createObjectURL(blob);
              //       const printer = printPdf(fileURL, true);
              //       if (printer) {
              //           printer.onclose = () => URL.revokeObjectURL(fileURL);
              //       }
              saveAs(blob, content.name);
              return true;
            }
          } catch (er) {
            self.notify.error(er);
            return false;
          }
        }
      }

      return false;
    }),

    emptyConfirm(): OrderConfirmSnapshotType {
      return {
        ...emptyConfirm(),
        order: self.filtersOrderId
          ? {
              id: self.filtersOrderId,
              inventoryNumber: "",
              name: "",
            }
          : null,
        orderId: self.filtersOrderId ? self.filtersOrderId : "",
      };
    },
  }))
  .named("OrderConfirmStore");

export type OrderConfirmStoreType = typeof OrderConfirmStore.Type;
export type OrderConfirmStoreSnapshotType = typeof OrderConfirmStore.SnapshotType;

const sortStorage = getSortOption(OrderConfirmStore.name);

export const initialState = (searchQuery = ""): OrderConfirmStoreSnapshotType => {
  const options = sortStorage({ column: DEFAULT_COLUMN, asc: DEFAULT_SORTING_ASCENDING_VALUE });

  return {
    confirms: [],
    orders: emptyOrders(),
    page: 1,
    pageSize: 40,
    totalCount: -1,
    sorter: {
      id: OrderConfirmStore.name,
      tableName: OrderConfirmStore.name,
      column: options.column,
      asc: options.asc,
    },
    renderer: {
      cache: {},
      id: "",
    },
    filtersType: "",
    filtersStatus: "",
    filtersOrderId: "",
    query: searchQuery,
    pureQuery: searchQuery.toLowerCase(),
  };
};

export const ExternalConfirmNumber = () => {
  const hash = trimStart("#", window.location.hash);
  const params = parse(hash);

  return (params["number"] as string) ?? "";
};
